import React, {useEffect, useMemo, useState} from 'react';
import useCustomForm from "../../../services/useCustomForm";
import {CButton, CInput} from "@coreui/react";
import Form, {FormApi} from "../../../components/form";
import {useParams} from "react-router-dom";
import {getApi} from "../../../apis/backendApis";
import useAlert from "../../../services/useAlert";
import ConfirmModal from "../../../components/confirmModal";
import '../../../scss/styles-tester.scss';

const normalizar = (valor: string) => (valor || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

const OperationPropagateForm = () => {

    const initialValues: any = {
        "id": null,
        "testCases": [],
        "currentOperation": []
    };
    const [, setFormApi] = useState<FormApi>();
    const [busca, setBusca] = useState<string>('');
    const [confirmando, setConfirmando] = useState<boolean>(false);
    const [showAlert] = useAlert();

    interface GetParam {
        id?: string
    }

    const {id} = useParams<GetParam>();

    const {
        values,
        handleSubmit,
        handleSubmitValues,
        setValues
    } = useCustomForm(
        {
            initialValues,
            route: 'operation/propagar',
            action: '',
            onSubmit: () => pedirConfirmacao(),
        }
    );

    useEffect(() => {
        getApi().get(`/operation/propagate/${id}`).then((response) => {
            setValues({...response.data});
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const testCases: Array<any> = useMemo(() => values['testCases'] || [], [values]);

    const visiveis = useMemo(() => {
        const termo = normalizar(busca);
        return testCases
            .map((testCase, indexTest) => ({testCase, indexTest}))
            .filter(({testCase}) => !termo || normalizar((testCase['codigo'] || '') + ' ' + (testCase['name'] || '')).includes(termo));
    }, [testCases, busca]);

    const selecionados = testCases.reduce((total, testCase) =>
        total + (testCase['operations'] || []).filter((row: any) => row['checked']).length, 0);
    const casosSelecionados = testCases.filter(testCase => (testCase['operations'] || []).some((row: any) => row['checked'])).length;

    const marcar = (alvo: (indexTest: number, index: number) => boolean, checked: boolean | ((atual: boolean) => boolean)) => {
        const novos = testCases.map((testCase, indexTest) => ({
            ...testCase,
            operations: (testCase['operations'] || []).map((row: any, index: number) =>
                alvo(indexTest, index)
                    ? {...row, checked: typeof checked === 'function' ? checked(!!row['checked']) : checked}
                    : row
            ),
        }));
        setValues({...values, testCases: novos});
    }

    const indicesVisiveis = new Set(visiveis.map(({indexTest}) => indexTest));
    const todosVisiveisMarcados = visiveis.length > 0 && visiveis.every(({testCase}) =>
        (testCase['operations'] || []).every((row: any) => row['checked']));

    const pedirConfirmacao = () => {
        if (selecionados === 0) {
            showAlert({msg: 'Selecione pelo menos um passo para receber a configuração.', type: 'warning', toaster: true});
            return;
        }
        setConfirmando(true);
    }

    const propagar = () => {
        setConfirmando(false);
        handleSubmitValues(values);
    }

    const operacaoAtual = values['currentOperation'] || {};

    return (<Form entity={'Propagação de Operação ' + id} handleSubmit={handleSubmit} onFormReady={setFormApi}>
        <div className="tester-secao mb-3" style={{height: 'auto'}}>
            <div className="tester-secao-titulo">Operação de origem</div>
            <dl className="tester-campo-leitura mb-2">
                <dt>Nome</dt>
                <dd>{operacaoAtual['name']}</dd>
            </dl>
            <pre className="tester-json mb-0" style={{maxHeight: '12rem'}}>{JSON.stringify(operacaoAtual['params'], null, 2)}</pre>
        </div>

        <div className="tester-filtros mb-3">
            <div className="tester-busca">
                <CInput type="search" value={busca} onChange={(e: any) => setBusca(e.target.value)}
                        placeholder="Filtrar casos por código ou nome" aria-label="Filtrar casos de teste"/>
            </div>
            <CButton color="primary" variant="outline"
                     onClick={() => marcar((indexTest) => indicesVisiveis.has(indexTest), !todosVisiveisMarcados)}
                     disabled={visiveis.length === 0}>
                {todosVisiveisMarcados ? 'Desmarcar' : 'Selecionar'} {busca ? 'os filtrados' : 'todos'}
            </CButton>
            <div className="tester-flex-espaco"/>
            <span className="tester-ajuda">
                <strong>{selecionados}</strong> passo(s) selecionado(s) em {casosSelecionados} caso(s) de teste
            </span>
        </div>

        {testCases.length === 0 && (
            <div className="tester-vazio">Nenhum caso de teste usa esta operação.</div>
        )}
        {testCases.length > 0 && visiveis.length === 0 && (
            <div className="tester-vazio">Nenhum caso com esse texto.</div>
        )}

        {visiveis.map(({testCase, indexTest}) => {
            const operacoes: Array<any> = testCase['operations'] || [];
            const todosDoCaso = operacoes.length > 0 && operacoes.every(row => row['checked']);
            return (
                <div key={indexTest} className="tester-secao mb-3 p-0" style={{height: 'auto'}}>
                    <label className="d-flex align-items-center m-0 px-3 py-2 bg-light border-bottom" style={{gap: '.5rem', cursor: 'pointer'}}>
                        <input type="checkbox" checked={todosDoCaso}
                               onChange={() => marcar((i) => i === indexTest, !todosDoCaso)}
                               aria-label={'Selecionar todos os passos de ' + testCase['name']}/>
                        {testCase['codigo'] && <span className="tester-codigo">{testCase['codigo']}</span>}
                        <strong className="text-truncate">{testCase['name']}</strong>
                    </label>
                    <div className="table-responsive">
                        <table className="table table-sm table-hover mb-0">
                            <thead>
                            <tr>
                                <th style={{width: '2.5rem'}}/>
                                <th style={{width: '4.5rem'}}>Ordem</th>
                                <th style={{width: '30%'}}>Nome</th>
                                <th>Configuração atual</th>
                            </tr>
                            </thead>
                            <tbody>
                            {operacoes.map((row: any, index: number) => (
                                <tr key={index} style={{cursor: 'pointer'}}
                                    onClick={() => marcar((i, j) => i === indexTest && j === index, atual => !atual)}>
                                    <td className="text-center align-middle">
                                        <input type="checkbox" checked={!!row['checked']} readOnly
                                               aria-label={'Selecionar passo ' + (row['index'] + 1)}/>
                                    </td>
                                    <td className="align-middle">{row['index'] + 1}</td>
                                    <td className="align-middle">{row['operation']['name']}</td>
                                    <td className="align-middle">
                                        <code className="d-block text-muted" style={{whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontSize: '.78rem'}}>
                                            {JSON.stringify(row['operation']['params'])}
                                        </code>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            );
        })}

        <ConfirmModal
            aberto={confirmando}
            titulo="Propagar configuração"
            rotuloConfirmar="Propagar"
            corConfirmar="primary"
            onConfirmar={propagar}
            onFechar={() => setConfirmando(false)}
        >
            <p className="mb-0">
                A configuração de <strong>{operacaoAtual['name']}</strong> vai substituir a de {selecionados} passo(s)
                em {casosSelecionados} caso(s) de teste. Os casos alterados passam a rodar com a configuração nova na próxima execução.
            </p>
        </ConfirmModal>
    </Form>)
}
export default OperationPropagateForm;
